import { Card, cn } from '@1trading/react';
import { ExternalLink } from 'lucide-react';
import { CopyButton } from '@/components/ui/copy-button';
import { LabeledValue } from '@/components/ui/labeled-value';
import { StatusChip } from '@/components/ui/status-chip';
import {
  CLIENT_TYPE_CHIP_VARIANTS,
  CLIENT_TYPE_LABELS,
  formatTypology,
  getRiskScoreClass,
  notInSlackReason,
  STATUS_CHIP_VARIANTS,
  STATUS_LABELS,
} from '@/features/market-surveillance/surveillance-meta';
import type { SurveillanceAlertDetail } from '@/services/api/market-surveillance';
import { formatDate } from '@/utils/format';
import { shortUuid } from '@/utils/string';

const EMPTY_PLACEHOLDER = <span className="text-muted-foreground">—</span>;

const CopyableId = ({ value }: { value: string }) => (
  <span className="inline-flex items-center gap-1" title={value}>
    {shortUuid(value)}
    <CopyButton label={`Copy ${value}`} value={value} />
  </span>
);
CopyableId.displayName = 'CopyableId';

interface AlertSummaryHeaderProps {
  detail: SurveillanceAlertDetail;
}

export const AlertSummaryHeader = ({ detail }: AlertSummaryHeaderProps) => {
  const { alert, status } = detail;

  return (
    <Card className="grid grid-cols-2 gap-4 p-4 md:grid-cols-5">
      <LabeledValue label="Alert ID" mono>
        <CopyableId value={alert.alertId} />
      </LabeledValue>
      <LabeledValue label="Typology">{formatTypology(alert.typology)}</LabeledValue>
      <LabeledValue label="Risk Score">
        <span className={cn('font-medium tabular-nums', getRiskScoreClass(alert.riskScore))}>{alert.riskScore}</span>
      </LabeledValue>
      <LabeledValue label="Triage Score">
        <span className="tabular-nums">{alert.triageScore}</span>
      </LabeledValue>
      <LabeledValue label="Status">
        <StatusChip className="px-2 py-0.5 text-xs" variant={STATUS_CHIP_VARIANTS[status.status]}>
          {STATUS_LABELS[status.status]}
        </StatusChip>
      </LabeledValue>
      <LabeledValue label="Detection Time">{formatDate(alert.createdAt)}</LabeledValue>
      <LabeledValue label="Instrument">{alert.instrument}</LabeledValue>
      <LabeledValue label="PID" mono>
        {alert.pid ? (
          <a className="inline-flex items-center gap-1 underline underline-offset-2" href={`/operations/users/${alert.pid}`} rel="noreferrer" target="_blank" title={alert.pid}>
            {shortUuid(alert.pid)}
            <ExternalLink className="size-3" />
          </a>
        ) : (
          EMPTY_PLACEHOLDER
        )}
      </LabeledValue>
      <LabeledValue label="Client Type">
        <StatusChip className="px-2 py-0.5 text-xs" variant={CLIENT_TYPE_CHIP_VARIANTS[alert.clientType]}>
          {CLIENT_TYPE_LABELS[alert.clientType]}
        </StatusChip>
      </LabeledValue>
      <LabeledValue label="Time Window">
        {alert.alertStart || alert.alertEnd ? `${alert.alertStart ? formatDate(alert.alertStart) : '—'} → ${alert.alertEnd ? formatDate(alert.alertEnd) : '—'}` : EMPTY_PLACEHOLDER}
      </LabeledValue>
      <LabeledValue label="Case ID" mono>
        <CopyableId value={alert.caseId} />
      </LabeledValue>
      {alert.actionable ? null : <p className="col-span-full text-sm text-muted-foreground">{notInSlackReason(alert.suppressedBy)}.</p>}
    </Card>
  );
};
AlertSummaryHeader.displayName = 'AlertSummaryHeader';
